import { useCallback, useRef } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmbedSettingsForm, type EmbedSettingsFormHandle } from './embed-settings-form';

interface EmbedSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialUrl?: string;
  initialHtml?: string;
  onSave: (url: string, html: string) => void;
}

export function EmbedSettingsDialog({
  open,
  onOpenChange,
  initialUrl = '',
  initialHtml = '',
  onSave,
}: EmbedSettingsDialogProps) {
  const formRef = useRef<EmbedSettingsFormHandle | null>(null);

  const handleSave = useCallback(() => {
    const values = formRef.current?.getValues();
    if (!values) return;
    onSave(values.url, values.html);
    onOpenChange(false);
  }, [onSave, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md">
        <DialogHeader>
          <DialogTitle>Embed Settings</DialogTitle>
          <DialogDescription>
            Add a URL or paste embed code to insert third-party content.
          </DialogDescription>
        </DialogHeader>

        <EmbedSettingsForm
          initialUrl={initialUrl}
          initialHtml={initialHtml}
          resetKey={open}
          ref={formRef}
        />

        <DialogFooter>
          <Button variant="outline" colorScheme="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
